// Which record is the notes dock looking at?
//
// The dock mounts once, in PageLayout's shared chrome (S13) — the only
// place that is genuinely on EVERY record page. ModeShell is not: it
// covers Opportunity / Processing / Ongoing Care / Service Record, while
// Household and Contact use a different shell entirely. Mounting once in
// the chrome means the dock lights up on all six without editing six
// pages, and can never drift out of sync between them.
//
// The trade that buys is that the dock has no props from the page, so it
// derives the record from the URL. That's deliberate and it's why this
// file is pure: the mapping is the part worth testing, and it's testable
// with no React, no router, and no DOM.
//
// Reserved sub-routes (/opportunities/board, /ongoing-care/plans,
// /processing/board, …) are excluded by REQUIRING the second segment to
// look like a UUID rather than by listing the words to skip. Every id in
// this system is a UUID, so the shape test is exact — and a sub-route
// added later can't silently start rendering a dock for a record named
// "forecast".

// Imported from shared/, NOT from web/src/api.ts, deliberately: this
// module is unit-tested from tests/, and the root tsconfig type-checks
// tests/ with Workers types while excluding web/. Reaching into api.ts
// would drag the whole browser-typed client into that project and break
// the build on `credentials` not existing in Workers' RequestInit.
import type { NoteEntitySegment } from "@shared/types/notes";

export interface NotesDockTarget {
  /** API path segment — the dock passes this straight to the notes CRUD. */
  kind: NoteEntitySegment;
  entityId: string;
}

/** First path segment ⇄ note kind. The segment IS the API segment for
 *  five of six; ongoing-care is the one place the URL and the API
 *  disagree (the route is /ongoing-care/:id, the notes live on
 *  rhythm-instances), which is exactly why this is a map and not a
 *  passthrough. */
const KIND_BY_ROUTE_SEGMENT: Record<string, NoteEntitySegment> = {
  households: "households",
  contacts: "contacts",
  "service-records": "service-records",
  opportunities: "opportunities",
  processing: "transactions",
  "ongoing-care": "rhythm-instances",
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Resolve the record a pathname is showing, or null when it isn't a
 * record page.
 *
 * Sub-routes of a record still count — /households/:id/snapshot and
 * /ongoing-care/:id/edit are both still that record, and someone reading
 * a snapshot has as much reason to jot a note as someone on the main
 * tab. Only the first two segments are consulted.
 */
export function notesDockTargetForPath(pathname: string): NotesDockTarget | null {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length < 2) return null;
  const kind = KIND_BY_ROUTE_SEGMENT[segments[0]!];
  if (!kind) return null;
  const entityId = segments[1]!;
  if (!UUID_RE.test(entityId)) return null;
  return { kind, entityId };
}
